import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import { useState, useMemo } from "react";
import NoteCard from "./NoteCard";
import AnalyticsService from "@/services/analytics";
import { CaretRight, CaretDown, FolderDotted } from "phosphor-react";
import { Search, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

type PublicContentProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  searchTerm?: string;
};

function PublicContent({
  venueDetailsResponse,
  searchTerm: propSearchTerm,
}: PublicContentProps) {
  const i18n = useI18N();
  const { isLoggedIn, user } = useAuth();
  const isMapper =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);

  // Use local state for non-mappers, prop for mappers
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const searchTerm = isMapper ? propSearchTerm || "" : localSearchTerm;

  const [selectedActionTitle, setSelectedActionTitle] = useState<string>("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "location">(
    "newest",
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Get public note actions from venue details
  const noteActions = useMemo(() => {
    return venueDetailsResponse?.details?.businessNotesActionsVenueNotes || [];
  }, [venueDetailsResponse]);

  // Get public notes
  const publicNotes = useMemo(() => {
    return venueDetailsResponse?.details?.businessNotesAll || [];
  }, [venueDetailsResponse]);

  // Filter note actions to only show those with notes
  const noteActionsWithNotes = useMemo(() => {
    return noteActions.filter((action) => {
      const notesCount = publicNotes.filter(
        (note) => note.action_title === action.title,
      ).length;
      return notesCount > 0;
    });
  }, [noteActions, publicNotes]);

  // Set initial action title
  useMemo(() => {
    if (noteActionsWithNotes.length > 0 && !selectedActionTitle) {
      setSelectedActionTitle(noteActionsWithNotes[0].title);
    }
  }, [noteActionsWithNotes, selectedActionTitle]);

  // Scroll to selected section
  const scrollToActionSection = (actionTitle: string) => {
    setSelectedActionTitle(actionTitle);
    // Hide mobile menu when filter is selected
    setShowMobileMenu(false);

    // Add a small delay to ensure the state is updated
    setTimeout(() => {
      const sectionId = `action-section-${actionTitle.toLowerCase().replace(/\s+/g, "-")}`;
      const element = document.getElementById(sectionId);
      if (element) {
        // Find the scrollable container
        const container = element.closest(".overflow-y-auto");

        if (container) {
          // Get accurate positioning using getBoundingClientRect
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();

          // Calculate the element's position relative to the scrollable container
          const relativeTop =
            elementRect.top - containerRect.top + container.scrollTop;

          // Scroll to position the section title near the top with proper padding
          const scrollTop = relativeTop - 32;

          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth",
          });
        } else {
          // Fallback: use browser's scrollIntoView
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      }
    }, 50);
  };

  // Filter notes by action title
  const filteredByAction = useMemo(() => {
    if (!selectedActionTitle) return publicNotes;
    return publicNotes.filter(
      (note) => note.action_title === selectedActionTitle,
    );
  }, [publicNotes, selectedActionTitle]);

  // Filter notes based on search term
  const filteredNotes = useMemo(() => {
    if (!searchTerm) {
      return filteredByAction;
    }

    // Split search term into words and remove empty strings
    const searchWords = searchTerm
      .toLowerCase()
      .split("")
      .filter((word) => word.trim() !== "");

    // Filter notes where all search words are present in note text, location, or title
    return filteredByAction.filter((note) => {
      const noteText = note.business_note_text
        ? note.business_note_text.toLowerCase()
        : "";
      const locationName = note.location_name
        ? note.location_name.toLowerCase()
        : "";
      const locationType = note.location_type
        ? note.location_type.toLowerCase()
        : "";
      const noteTitle = note.business_note_title
        ? note.business_note_title.toLowerCase()
        : "";

      const combinedText = `${noteText} ${locationName} ${locationType} ${noteTitle}`;
      return searchWords.every((word) => combinedText.includes(word));
    });
  }, [filteredByAction, searchTerm]);

  // Sort notes
  const sortedNotes = useMemo(() => {
    const notes = [...filteredNotes];

    if (sortBy === "newest") {
      // Most recent
      return notes.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } else if (sortBy === "oldest") {
      // Oldest
      return notes.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    } else if (sortBy === "location") {
      // By location
      return notes.sort((a, b) =>
        a.location_type.localeCompare(b.location_type),
      );
    }

    return notes;
  }, [filteredNotes, sortBy]);

  return (
    <>
      {noteActionsWithNotes.length > 0 ? (
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <aside
            className={`w-full md:w-64 lg:w-72 bg-bg-brand-secondary-default border-b md:border-b-0 md:border-r border-border-token-neutral-minimal overflow-y-auto flex-shrink-0 ${showMobileMenu ? "flex py-6 max-h-[50vh]" : "hidden md:flex md:py-6"
              }`}
            aria-label={i18n.get("typeFilter")}
          >
            <nav>
              <ul>
                {noteActionsWithNotes.map((action) => {
                  return (
                    <li key={action.title} className="relative">
                      <button
                        type="button"
                        onClick={() => scrollToActionSection(action.title)}
                        aria-current={selectedActionTitle === action.title ? 'true' : undefined}
                        className={`w-full flex items-center gap-3 pl-[52px] pr-4 py-3 cursor-pointer relative group focus-blue ${selectedActionTitle === action.title
                          ? "bg-container-brand-minimal"
                          : "bg-bg-brand-secondary-default"
                          }`}
                      >
                        {/* Vertical bar indicator */}
                        <div
                          className={`absolute left-[27px] top-0 bottom-0 w-[2px] transition-colors ${selectedActionTitle === action.title
                            ? "bg-[#352FC3]"
                            : "bg-[#DEDDF7] group-hover:bg-[#352FC3]"
                            }`}
                        ></div>

                        {/* Action title and count */}
                        <span
                          className={`flex-1 font-medium label-m-medium text-left ${selectedActionTitle === action.title
                            ? "text-fg-brand-subtle"
                            : "text-fg-neutral-strong"
                            }`}
                        >
                          {action.title}
                          {/* ({notesCount}) */}
                        </span>

                        {/* Caret icon */}
                        <CaretRight size={16} className="text-gray-600" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
            {/* Mobile Menu Button - Only show when sidebar is collapsed */}
            {!showMobileMenu && sortedNotes.length > 0 && (
              <div className="md:hidden mb-4">
                <button
                  onClick={() => setShowMobileMenu(true)}
                  className="flex items-center gap-2 text-fg-brand-subtle font-medium"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                  Menu
                </button>
              </div>
            )}

            {/* Top Action Bar */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
              {/* Search Bar - Only for non-mapper users */}
              {!isMapper && (
                <div className="relative w-full md:w-64">
                  <input
                    type="text"
                    placeholder={i18n.get("spaceName")}
                    value={localSearchTerm}
                    onChange={(e) => setLocalSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pr-10 rounded-lg border border-border-token-neutral-minimal title-s-semibold text-fg-neutral-strong bg-bg-brand-secondary-default placeholder:text-fg-neutral-minimal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    aria-label="Search by space name"
                  />
                  {localSearchTerm ? (
                    <X
                      size={20}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal cursor-pointer hover:text-fg-neutral-subtle"
                      onClick={() => setLocalSearchTerm("")}
                    />
                  ) : (
                    <Search
                      size={20}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal"
                    />
                  )}
                </div>
              )}

              <div className="flex items-center justify-between w-full md:w-auto md:flex-1">
                <span className="label-m-medium text-fg-neutral-subtle">
                  {sortedNotes.length}{" "}
                  {sortedNotes.length === 1 ? i18n.get("result") : i18n.get("results")}
                </span>

                {/* Sort Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger className="focus:outline-none focus-visible:outline-none">
                    <div className="flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md cursor-pointer focus-blue">
                      <span className="underline">
                        {sortBy === "newest" && i18n.get("dateAddedNewest")}
                        {sortBy === "oldest" && i18n.get("dateAddedOldest")}
                        {sortBy === "location" && i18n.get("byLocation")}
                      </span>
                      <CaretDown size={16} />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel aria-label="Sort by">
                      {i18n.get("sortBy")}:
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuRadioGroup
                      value={sortBy}
                      onValueChange={(value) => {
                        setSortBy(value as "newest" | "oldest" | "location");
                        AnalyticsService.trackGAEventHandler("alertsTabSortChanged");
                        AnalyticsService.trackMx("alertsTabSortChanged", { SortBy: value });
                      }}
                    >
                      <DropdownMenuRadioItem value="newest">
                        {i18n.get("dateAddedNewest")}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="oldest">
                        {i18n.get("dateAddedOldest")}
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="location">
                        {i18n.get("byLocation")}
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Grouped Notes by Action Title */}

            <div className="space-y-8">
              {noteActionsWithNotes.map((action) => {
                const actionNotes = sortedNotes.filter(
                  (note) => note.action_title === action.title,
                );
                const sectionId = `action-section-${action.title.toLowerCase().replace(/\s+/g, "-")}`;

                return (
                  <section
                    key={action.title}
                    id={sectionId}
                    className="space-y-4"
                  >
                    {/* Section Header */}
                    <h3 className="title-s-semibold text-fg-neutral-strong">
                      {action.title} ({actionNotes.length})
                    </h3>

                    {/* Notes List */}
                    {actionNotes.length > 0 ? (
                      <div className="space-y-4">
                        {actionNotes.map((note) => {
                          // Convert businessNotesAll format to BusinessNote format for NoteCard
                          const convertedNote = {
                            business_notes_id: note.business_note_text_id,
                            business_notes_business_id: 0,
                            notes: note.business_note_text || "",
                            business_notes_created_at: note.created_at,
                            business_notes_updated_at: note.created_at,
                            status: "",
                            business_notes_action_id: 0,
                            business_notes_task_id: 0,
                            creator_user_name:
                              note.business_note_by_user || "Unknown",
                            creator_user_id: 0,
                            latest_editor_user_id: null,
                            latest_editor_user_name: null,
                            locationAreasData: [
                              {
                                locationType: note.location_type,
                                locationId: 0,
                                locationName: note.location_name,
                                categoryHierarchy: "",
                                categoryIdHierarchy: "",
                                businessNoteAreaId: 0,
                              },
                            ],
                            schedule_date_from: note.schedule_date_from,
                            schedule_date_to: note.schedule_date_to,
                            formatedScheduleDateFrom:
                              note.formatedScheduleDateFrom,
                            formatedScheduleDateTo: note.formatedScheduleDateTo,
                            business_note_title: note.business_note_title,
                          };

                          return (
                            <NoteCard
                              key={note.business_note_text_id}
                              note={convertedNote}
                              showLocationInfo={true}
                              venueName={venueDetailsResponse?.details?.name}
                              noteType="Public"
                            />
                          );
                        })}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>

          </main>
        </div>
      ) : (
        <>
          <div className="h-screen flex flex-col">
            {/* Secondary Filter Tabs */}
            <nav
              className="bg-white border-b border-border-token-neutral-minimal px-4 py-4 md:px-6 lg:px-8 flex-shrink-0"
              aria-label="Review filters"
            >
              <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                <ul className="flex gap-4 md:gap-6 whitespace-nowrap overflow-x-auto">
                  <li key="alert_all">
                    <button className="px-3 py-2 rounded-md focus-blue label-m-medium text-fg-brand-strong bg-container-brand-minimal">
                      All (0)
                    </button>
                  </li>
                </ul>

                {/* Search Bar and Sort Dropdown */}
                <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:ml-auto w-full md:w-auto">
                  <div className="relative w-full md:w-64">
                    <label htmlFor="reviews-search" className="sr-only">
                      {i18n.get("searchReviews")}
                    </label>
                    <input
                      id="reviews-search"
                      type="text"
                      placeholder={i18n.get("spaceName")}
                      value={localSearchTerm}
                      onChange={(e) => setLocalSearchTerm(e.target.value)}
                      className="w-full  h-12  px-4 py-2 pr-10 rounded-lg border border-border-token-neutral-minimal title-s-semibold text-fg-neutral-strong bg-bg-brand-secondary-default placeholder:text-fg-neutral-minimal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {localSearchTerm ? (
                      <button
                        type="button"
                        onClick={() => setLocalSearchTerm("")}
                        aria-label="Clear search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-fg-neutral-minimal hover:text-fg-neutral-subtle focus-blue"
                      >
                        <X size={20} aria-hidden="true" />
                      </button>
                    ) : (
                      <Search
                        size={20}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal"
                        aria-hidden="true"
                      />
                    )}
                  </div>

                  {/* Sort Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger className="focus:outline-none focus-visible:outline-none">
                      <div className="flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md cursor-pointer focus-blue">
                        <span className="underline">
                          {sortBy === "newest" && i18n.get("dateAddedNewest")}
                          {sortBy === "oldest" && i18n.get("dateAddedOldest")}
                          {sortBy === "location" && i18n.get("byLocation")}
                        </span>
                        <CaretDown size={16} />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel aria-label="Sort by">
                        {i18n.get("sortBy")}:
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        value={sortBy}
                        onValueChange={(value) => {
                          setSortBy(value as "newest" | "oldest" | "location");
                          AnalyticsService.trackGAEventHandler("alertsTabSortChanged");
                          AnalyticsService.trackMx("alertsTabSortChanged", { SortBy: value });
                        }}
                      >
                        <DropdownMenuRadioItem value="newest">
                          {i18n.get("dateAddedNewest")}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="oldest">
                          {i18n.get("dateAddedOldest")}
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="location">
                          {i18n.get("byLocation")}
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </nav>

            {/* No reviews found Content */}
            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              <div className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
                <div className="text-center py-12" role="status" aria-live="polite">
                  <div className="flex flex-1 flex-col justify-center items-center px-4">
                    <FolderDotted size={32} className="text-fg-neutral-strong" aria-hidden="true" />
                    <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                        {i18n.get("alerts")}
                      </p>
                      <p className="text-center label-s-regular text-fg-neutral-subtle mt-2 max-w-sm">
                        {searchTerm
                          ? i18n.get("noNotesFoundMatchingSearch")
                          : i18n.get("noPublicNotesAvailable")}
                      </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default PublicContent;
